/** The Sightmap property grammar: what a declared property's `extract` reads.
 * `path` addresses a descendant definition by name segments and reads its `prop`. */
export type PropertySource =
  | { kind: 'text' }
  | { kind: 'raw_text' }
  | { kind: 'attr'; name: string }
  | { kind: 'state'; name: string }
  | { kind: 'exists'; path: string[] }
  | { kind: 'path'; path: string[]; prop: string }
  | { kind: 'invalid'; raw: string }

export interface PropertyTraits {
  /** Styling, never identity. */
  presentation: boolean
  /** Restates content the tree already carries. */
  derivedText: boolean
}

const ATTR = 'attr='
const STATE = 'state:'
const EXISTS = 'exists:'
// Legacy: mixed-case prefixes are no `attr` source but were rejected as presentation.
const LEGACY_PRESENTATION = /^attr=(?:class|style)$/i

/** Total. `formatExtract(parseExtract(s)) === s` for every `s`. */
export function parseExtract(extract: string): PropertySource {
  if (extract === 'text' || extract === 'raw_text') return { kind: extract }
  if (extract.startsWith(ATTR)) return { kind: 'attr', name: extract.slice(ATTR.length) }
  if (extract.startsWith(STATE)) return { kind: 'state', name: extract.slice(STATE.length) }
  if (extract.startsWith(EXISTS)) return { kind: 'exists', path: extract.slice(EXISTS.length).split('.') }
  const path = extract.split('.')
  if (path.length < 2) return { kind: 'invalid', raw: extract }
  const prop = path.pop()!
  return { kind: 'path', path, prop }
}

/** Left inverse of `parseExtract`. Sources built from names need not re-parse to the same value: compare formatted strings. */
export function formatExtract(source: PropertySource): string {
  switch (source.kind) {
    case 'text':
    case 'raw_text': return source.kind
    case 'attr': return ATTR + source.name
    case 'state': return STATE + source.name
    case 'exists': return EXISTS + source.path.join('.')
    case 'path': return [...source.path, source.prop].join('.')
    case 'invalid': return source.raw
    default: {
      const unhandled: never = source
      throw new Error(`unhandled property source: ${JSON.stringify(unhandled)}`)
    }
  }
}

export function propertyTraits(source: PropertySource): PropertyTraits {
  switch (source.kind) {
    case 'text':
    case 'raw_text': return { presentation: false, derivedText: true }
    case 'attr': return { presentation: /^(?:class|style)$/i.test(source.name), derivedText: false }
    case 'invalid': return { presentation: LEGACY_PRESENTATION.test(source.raw), derivedText: false }
    case 'state':
    case 'exists':
    case 'path': return { presentation: false, derivedText: false }
    default: {
      const unhandled: never = source
      throw new Error(`unhandled property source: ${JSON.stringify(unhandled)}`)
    }
  }
}

/** Re-address sources that read through the definition named `from` after it is renamed to `to`. */
export function renamePathHead(source: PropertySource, from: string, to: string): PropertySource {
  const head = from.split('.')
  switch (source.kind) {
    case 'text':
    case 'raw_text':
    case 'attr':
    case 'state':
    case 'invalid': return source
    case 'exists': return sameSegments(source.path, head) ? { ...source, path: [to] } : source
    case 'path': return startsWithSegments(source.path, head) ? { ...source, path: [to, ...source.path.slice(head.length)] } : source
    default: {
      const unhandled: never = source
      throw new Error(`unhandled property source: ${JSON.stringify(unhandled)}`)
    }
  }
}

function sameSegments(a: string[], b: string[]): boolean {
  return a.length === b.length && startsWithSegments(a, b)
}

function startsWithSegments(path: string[], prefix: string[]): boolean {
  return prefix.length <= path.length && prefix.every((segment, i) => path[i] === segment)
}
